import { formatDateOnlyForPicker } from './dateOnly';
import {
  EMPTY_INVOICE_DATE_RANGE_FILTER,
  areInvoiceDateRangeFiltersEqual,
  legacyDueDateToRangeFilter,
  normalizeInvoiceDateRangeFilter,
  resolveInvoiceDateRangeForApi,
} from './invoiceDateRangeFilter';

export const INVOICE_LIST_URL_PARAM_KEYS = [
  'search',
  'page',
  'pageSize',
  'sort',
  'payment',
  'invoiceStatus',
  'dueDatePreset',
  'dueDateStart',
  'dueDateEnd',
  'invoiceDatePreset',
  'invoiceDateStart',
  'invoiceDateEnd',
  'disputed',
];

export const INVOICE_LIST_SEARCH_PLACEHOLDER = 'Search Property, Operator, or Invoice..';

export const DEFAULT_INVOICE_LIST_EXTRA_FILTERS = {
  selectedPayment: [],
  selectedInvoiceStatus: [],
  selectedDueDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  selectedInvoiceDateRange: { ...EMPTY_INVOICE_DATE_RANGE_FILTER },
  selectedDisputed: [],
};

/** Disputed dropdown — values match BE invoice list filter (`dispute` / empty string). */
export const INVOICE_DISPUTED_FILTER_OPTIONS = [
  { text: 'Yes', value: 'dispute' },
  { text: 'No', value: '' },
];

export const getInvoiceDisputedFilterLabel = (value) => {
  if (value === 'dispute') {
    return 'Yes';
  }
  if (value === '') {
    return 'No';
  }
  if (value === 'updated') {
    return 'Updated';
  }
  return value;
};

function parseDateRangeFromParams(searchParams, prefix, fallbackRange) {
  const preset = searchParams.get(`${prefix}Preset`) || fallbackRange?.preset || null;
  const startDate = formatDateOnlyForPicker(
    searchParams.get(`${prefix}Start`) || fallbackRange?.startDate || '',
  );
  const endDate = formatDateOnlyForPicker(
    searchParams.get(`${prefix}End`) || fallbackRange?.endDate || '',
  );

  if (!preset && !startDate && !endDate) {
    return normalizeInvoiceDateRangeFilter(fallbackRange);
  }

  if (!preset && (startDate || endDate)) {
    return normalizeInvoiceDateRangeFilter({
      preset: 'custom',
      startDate,
      endDate,
    });
  }

  return normalizeInvoiceDateRangeFilter({
    preset,
    startDate,
    endDate,
  });
}

export const parseInvoiceFilters = (searchParams, _paramKey, fallback) => {
  let selectedDueDateRange = parseDateRangeFromParams(
    searchParams,
    'dueDate',
    fallback.selectedDueDateRange,
  );

  if (!selectedDueDateRange.preset && searchParams.get('dueDate')) {
    selectedDueDateRange = legacyDueDateToRangeFilter(searchParams.get('dueDate'));
  } else if (!selectedDueDateRange.preset && fallback.selectedDueDate) {
    selectedDueDateRange = legacyDueDateToRangeFilter(fallback.selectedDueDate);
  }

  return {
    selectedPayment: searchParams.get('payment')
      ? searchParams.get('payment').split(',').filter(Boolean)
      : fallback.selectedPayment,
    selectedInvoiceStatus: searchParams.get('invoiceStatus')
      ? searchParams.get('invoiceStatus').split(',').filter(Boolean)
      : fallback.selectedInvoiceStatus,
    selectedDueDateRange,
    selectedInvoiceDateRange: parseDateRangeFromParams(
      searchParams,
      'invoiceDate',
      fallback.selectedInvoiceDateRange,
    ),
    selectedDisputed: searchParams.get('disputed')
      ? searchParams.get('disputed').split(',').filter(Boolean)
      : fallback.selectedDisputed,
  };
};

function serializeDateRange(prefix, range) {
  const normalized = normalizeInvoiceDateRangeFilter(range);
  if (!normalized.preset) {
    return {
      [`${prefix}Preset`]: '',
      [`${prefix}Start`]: '',
      [`${prefix}End`]: '',
    };
  }

  if (normalized.preset !== 'custom') {
    return {
      [`${prefix}Preset`]: normalized.preset,
      [`${prefix}Start`]: '',
      [`${prefix}End`]: '',
    };
  }

  const resolved = resolveInvoiceDateRangeForApi(normalized);
  return {
    [`${prefix}Preset`]: 'custom',
    [`${prefix}Start`]: resolved.startDate || '',
    [`${prefix}End`]: resolved.endDate || '',
  };
}

export const serializeInvoiceFilters = (filters) => ({
  payment:
    Array.isArray(filters.selectedPayment) && filters.selectedPayment.length > 0
      ? filters.selectedPayment.join(',')
      : '',
  invoiceStatus:
    Array.isArray(filters.selectedInvoiceStatus) && filters.selectedInvoiceStatus.length > 0
      ? filters.selectedInvoiceStatus.join(',')
      : '',
  ...serializeDateRange('dueDate', filters.selectedDueDateRange),
  ...serializeDateRange('invoiceDate', filters.selectedInvoiceDateRange),
  disputed:
    Array.isArray(filters.selectedDisputed) && filters.selectedDisputed.length > 0
      ? filters.selectedDisputed.join(',')
      : '',
});

export const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export { areInvoiceDateRangeFiltersEqual, resolveInvoiceDateRangeForApi };

export function buildInvoiceListRequestFilter({
  filter,
  selectedPayment,
  selectedInvoiceStatus,
  selectedDueDateRange,
  selectedInvoiceDateRange,
  selectedDisputed,
}) {
  return {
    ...filter,
    searchText: (filter?.searchText || '').trim(),
    selectedPayment,
    selectedInvoiceStatus,
    selectedDueDateRange: resolveInvoiceDateRangeForApi(selectedDueDateRange),
    selectedInvoiceDate: resolveInvoiceDateRangeForApi(selectedInvoiceDateRange),
    selectedUpdateStatus: [],
    selectedDisputed,
  };
}
